import { useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Palette, Upload, Loader2, Check, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const fileUrl = (path) => (path ? `${BACKEND_URL}${path}` : "");

export default function Branding() {
  const { user, refresh } = useAuth();
  const branding = user.agency?.branding || {};
  const [color, setColor] = useState(branding.accent_color || "#FF5C00");
  const [logo, setLogo] = useState(branding.logo_url || "");
  const [savingColor, setSavingColor] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const saveColor = async () => {
    setSavingColor(true);
    try {
      await api.put("/agency/branding", { accent_color: color });
      toast.success("Cor de marca guardada!");
      await refresh();
    } catch (e) { toast.error("Erro ao guardar"); }
    finally { setSavingColor(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/agency/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogo(data.logo_url);
      toast.success("Logótipo carregado!");
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro no upload"); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="branding-page">
      <div>
        <h1 className="font-heading font-black text-2xl sm:text-3xl flex items-center gap-2">
          <Palette className="text-brand-blue" /> Marca da agência
        </h1>
        <p className="text-brand-muted text-sm mt-1">
          Personalize a experiência dos seus membros com o logótipo e cor da {user.agency?.name || "sua agência"}.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Logo */}
        <div className="p-6 rounded-sm bg-brand-surface border border-brand-border">
          <h2 className="font-heading font-semibold flex items-center gap-2"><ImageIcon size={18} className="text-brand-orange" /> Logótipo</h2>
          <div className="mt-4 h-32 rounded-sm border border-dashed border-brand-border flex items-center justify-center bg-background overflow-hidden">
            {logo ? (
              <img src={fileUrl(logo)} alt="Logótipo" className="max-h-24 max-w-[80%] object-contain" data-testid="branding-logo-preview" />
            ) : (
              <span className="text-sm text-brand-muted">Sem logótipo</span>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={upload} data-testid="branding-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="branding-upload-button"
            className="w-full mt-4 bg-brand-blue hover:bg-brand-bluehover rounded-sm font-bold">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload size={15} className="mr-1" /> Carregar logótipo</>}
          </Button>
          <p className="text-xs text-brand-muted mt-2">PNG, JPG, WEBP ou SVG · máx 3MB</p>
        </div>

        {/* Accent color */}
        <div className="p-6 rounded-sm bg-brand-surface border border-brand-border">
          <h2 className="font-heading font-semibold flex items-center gap-2"><Palette size={18} className="text-brand-orange" /> Cor de destaque</h2>
          <div className="mt-4 flex items-center gap-4">
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} data-testid="branding-color-input"
              className="h-16 w-16 rounded-sm bg-transparent border border-brand-border cursor-pointer" />
            <div>
              <p className="font-mono text-sm">{color}</p>
              <p className="text-xs text-brand-muted">Aplicada aos destaques da app dos membros.</p>
            </div>
          </div>
          {/* preview swatch */}
          <div className="mt-4 p-3 rounded-sm border border-brand-border flex items-center gap-3" style={{ borderColor: color }}>
            <div className="h-8 w-8 rounded-sm" style={{ background: color }} />
            <span className="text-sm font-bold" style={{ color }}>Pré-visualização do destaque</span>
          </div>
          <Button onClick={saveColor} disabled={savingColor} data-testid="branding-save-color-button"
            className="w-full mt-4 bg-brand-orange hover:bg-brand-orangehover rounded-sm font-bold">
            {savingColor ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check size={15} className="mr-1" /> Guardar cor</>}
          </Button>
        </div>
      </div>

      <p className="text-xs text-brand-muted">
        As personalizações aplicam-se aos membros desta agência. Cada agência da rede pode definir a sua própria marca.
      </p>
    </div>
  );
}
